import { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import { Home, ShoppingCart, Users, Package, Settings, BarChart3, Grid3x3, Clock, Phone, LogOut } from "lucide-react";

// Pages
import OrderPage from "@/pages/OrderPage";
import AdminPage from "@/pages/AdminPage";
import TablesPage from "@/pages/TablesPage";
import CustomerPage from "@/pages/CustomerPage";
import ReportsPage from "@/pages/ReportsPage";
import PhonePage from "@/pages/PhonePage";
import LoginPage from "@/pages/LoginPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = "/api";

// Layout Component
const Layout = ({ children, onLogout }) => {
  const navigate = useNavigate();
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const username = localStorage.getItem("username") || "Admin";

useEffect(() => {
  const checkVersion = async () => {
    try {
      const res = await fetch("/version.txt", { cache: "no-store" });
      const serverVersion = (await res.text()).trim();
      const localVersion = localStorage.getItem("__app_version__");

      if (!localVersion) {
        localStorage.setItem("__app_version__", serverVersion);
        return;
      }

      if (localVersion !== serverVersion) {
        localStorage.setItem("__app_version__", serverVersion);

        // Cache temizle (tarayıcı offline cache dahil)
        if ("caches" in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }

        // Yeni build'i almak için agresif reload
        const url = new URL(window.location.href);
        url.searchParams.set("v", Date.now().toString());
        window.location.replace(url.toString());
      }
    } catch (e) {
      console.log("Version check error:", e);
    }
  };

  checkVersion();
}, []);

  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, [window.location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("username");
    onLogout();
  };

  const menuItems = [
    { path: "/", label: "Ana Sayfa", icon: Home },
    { path: "/telefon", label: "Telefon", icon: Phone },
    { path: "/siparis", label: "Sipariş", icon: ShoppingCart },
    { path: "/masalar", label: "Masalar", icon: Grid3x3 },
    { path: "/musteri", label: "Müşteri", icon: Users },
    { path: "/raporlar", label: "Raporlar", icon: BarChart3 },
    { path: "/admin", label: "Yönetim", icon: Settings },
  ];

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className="w-64 bg-gradient-to-b from-orange-600 to-orange-700 text-white flex flex-col shadow-xl">
        <div className="p-6 border-b border-orange-500">
          <h1 className="text-2xl font-bold">🍕 Verona Pizza</h1>
          <p className="text-xs text-orange-200 mt-1">Restoran Yönetim Sistemi</p>
          <p className="text-xs text-orange-100 mt-2 flex items-center">
            <Users size={14} className="mr-1" />
            {username}
          </p>
        </div>
        <nav className="flex-1 p-4 space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentPath === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? "bg-white text-orange-600 shadow-lg"
                    : "text-white hover:bg-orange-500"
                }`}
                onClick={() => setCurrentPath(item.path)}
              >
                <Icon size={20} />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>
        
        {/* Logout Button */}
        <div className="p-4 border-t border-orange-500">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg transition font-medium shadow-lg"
          >
            <LogOut size={20} />
            <span>Çıkış Yap</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {children}
      </div>
    </div>
  );
};

// Dashboard Page
const Dashboard = () => {
  const [stats, setStats] = useState({
    todayOrders: 0,
    todayRevenue: 0,
    activeTables: 0,
    pendingOrders: 0
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const [ordersRes, tablesRes, reportRes] = await Promise.all([
        axios.get(`${API}/orders?date=${today}`),
        axios.get(`${API}/tables`),
        axios.get(`${API}/reports/daily?date=${today}`)
      ]);

      const activeTables = tablesRes.data.filter(t => t.status === 'dolu').length;
      // Only count unpaid orders as pending
      const pendingOrders = ordersRes.data.filter(
        o => o.payment_status === "ödenmedi"
      ).length;

      setStats({
        todayOrders: reportRes.data.total_orders,
        todayRevenue: reportRes.data.total_revenue,
        activeTables,
        pendingOrders
      });
    } catch (error) {
      console.error('İstatistikler yüklenirken hata:', error);
    }
  };

  const statCards = [
    { label: "Bugünkü Sipariş", value: stats.todayOrders, color: "bg-blue-500", icon: ShoppingCart },
    { label: "Bugünkü Ciro", value: `${stats.todayRevenue.toFixed(2)} ₺`, color: "bg-green-500", icon: BarChart3 },
    { label: "Dolu Masalar", value: stats.activeTables, color: "bg-purple-500", icon: Grid3x3 },
    { label: "Bekleyen Sipariş", value: stats.pendingOrders, color: "bg-orange-500", icon: Clock },
  ];

  return (
    <div className="p-8" data-testid="dashboard">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">Hoş Geldiniz! 👋</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <div key={idx} className={`${stat.color} rounded-xl p-6 text-white shadow-lg`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">{stat.label}</p>
                  <p className="text-3xl font-bold mt-2">{stat.value}</p>
                </div>
                <Icon size={40} className="opacity-80" />
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/telefon" className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all text-center">
          <Phone size={48} className="mx-auto text-blue-600 mb-4" />
          <h3 className="text-xl font-bold text-gray-800">Telefon Siparişleri</h3>
          <p className="text-gray-600 mt-2">Telefon siparişleri al</p>
        </Link>

        <Link to="/siparis" className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all text-center">
          <ShoppingCart size={48} className="mx-auto text-orange-600 mb-4" />
          <h3 className="text-xl font-bold text-gray-800">Yeni Sipariş</h3>
          <p className="text-gray-600 mt-2">Sipariş almaya başla</p>
        </Link>

        <Link to="/masalar" className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all text-center">
          <Grid3x3 size={48} className="mx-auto text-purple-600 mb-4" />
          <h3 className="text-xl font-bold text-gray-800">Masalar</h3>
          <p className="text-gray-600 mt-2">Masa durumlarını gör</p>
        </Link>
      </div>
    </div>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [incomingCall, setIncomingCall] = useState(null);
  const [isCallerIDConnected, setIsCallerIDConnected] = useState(false);
  const [persistentPhoneNumber, setPersistentPhoneNumber] = useState(""); // Telefon numarası global state


  // Caller ID WebSocket stabilite için (heartbeat + reconnect backoff)
  const wsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isIntentionallyClosedRef = useRef(false);
  const lastMessageAtRef = useRef(0);
  const lastPongAtRef = useRef(0);


  // Caller ID polling fallback (WS kaçırırsa bile numarayı yakala)
  const [callerPollingAfter, setCallerPollingAfter] = useState(null); // ISO timestamp
  const processedCallIdsRef = useRef(new Set());


  // Global idle temizleme: Telefon sekmesinde sipariş alınmasa bile numara state'i takılı kalmasın.
  // B seçeneği: diğer sayfalarda da çalışır.
  const [globalLastInteractionAt, setGlobalLastInteractionAt] = useState(Date.now());

  const touchGlobalInteraction = () => {
    setGlobalLastInteractionAt(Date.now());
  };

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem("auth_token");
      if (token) {
        setIsAuthenticated(true);
      }
      setIsCheckingAuth(false);
    };
    checkAuth();
  }, []);

  // 3 dk idle -> global call state temizle
  useEffect(() => {
    const interval = setInterval(() => {
      const idleMs = Date.now() - globalLastInteractionAt;
      if (idleMs < 180000) return;

      // Herhangi bir çağrı state'i yoksa dokunma
      if (!persistentPhoneNumber && !incomingCall) return;

      console.log('[CallerID] 3dk idle -> global clear phone state');

      // Global state temizle
      setPersistentPhoneNumber("");
      setIncomingCall(null);

      // localStorage temizle
      try {
        const latestCallId = localStorage.getItem('latestCallId');
        if (latestCallId) {
          localStorage.removeItem(latestCallId);
          localStorage.removeItem('latestCallId');
        }
      } catch (e) {
        // ignore
      }

      // Timer'ı resetle (tekrar tekrar çalışmasın)
      setGlobalLastInteractionAt(Date.now());
    }, 5000);

    return () => clearInterval(interval);
  }, [globalLastInteractionAt, persistentPhoneNumber, incomingCall]);

  // Global WebSocket - only when authenticated (stabil: heartbeat + backoff reconnect)
  useEffect(() => {
    if (!isAuthenticated) return;

    // Önceki state'leri sıfırla
    isIntentionallyClosedRef.current = false;
    reconnectAttemptsRef.current = 0;
    lastMessageAtRef.current = Date.now();
    lastPongAtRef.current = Date.now();

    const wsProto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${wsProto}//${window.location.host}/api/ws/caller-id`;

    const cleanupSocket = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (e) {
          // ignore
        }
        wsRef.current = null;
      }
    };

    const scheduleReconnect = (reason) => {
      if (isIntentionallyClosedRef.current) return;

      // Backoff (max ~30s)
      reconnectAttemptsRef.current += 1;
      const attempt = reconnectAttemptsRef.current;
      const delay = Math.min(30000, 1000 * Math.pow(2, attempt));

      console.log(`[CallerID] Reconnect planlandı (attempt=${attempt}, delay=${delay}ms). reason=${reason}`);

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      reconnectTimeoutRef.current = setTimeout(() => {
        connectWebSocket();
      }, delay);
    };

    const connectWebSocket = () => {
      if (isIntentionallyClosedRef.current) return;

      cleanupSocket();

      try {
        console.log('[CallerID] WebSocket bağlanıyor:', wsUrl);
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log('✅ [CallerID] WebSocket bağlandı');
          setIsCallerIDConnected(true);
          reconnectAttemptsRef.current = 0;
          lastMessageAtRef.current = Date.now();
          lastPongAtRef.current = Date.now();

          // Heartbeat
          pingIntervalRef.current = setInterval(() => {
            const now = Date.now();

            // Uzun süre pong gelmediyse bağlantı “zombi” olabilir -> reconnect
            if (now - lastPongAtRef.current > 60000) {
              console.warn('[CallerID] Pong timeout (60s) -> reconnect');
              try {
                ws.close();
              } catch (e) {
                // ignore
              }
              return;
            }

            if (ws.readyState === WebSocket.OPEN) {
              try {
                ws.send('ping');
              } catch (e) {
                console.warn('[CallerID] Ping gönderilemedi:', e);
              }
            }
          }, 15000);
        };

        ws.onmessage = (event) => {
          lastMessageAtRef.current = Date.now();

          if (event.data === 'ping') {
            // Server ping atarsa pong dön
            lastPongAtRef.current = Date.now();
            try {
              ws.send('pong');
            } catch (e) {
              // ignore
            }
            return;
          }

          if (event.data === 'pong') {
            lastPongAtRef.current = Date.now();
            return;
          }

          try {
            const data = JSON.parse(event.data);
            if (data.type !== 'incoming_call') return;

            const incomingPhone = data.phone_number;
            const timestamp = Date.now();
            const callId = data.call_id || `ws_${timestamp}`;

            // Dedup (WS + polling aynı çağrıyı getirebilir)
            if (processedCallIdsRef.current.has(callId)) {
              return;
            }
            processedCallIdsRef.current.add(callId);

            // Polling anchor'ı güncelle
            if (data.timestamp) {
              setCallerPollingAfter(data.timestamp);
            }

            // localStorage'a kaydet
            try {
              const callData = { phoneNumber: incomingPhone, timestamp, callId, processed: false };
              localStorage.setItem(callId, JSON.stringify(callData));
              localStorage.setItem('latestCallId', callId);
            } catch (e) {
              console.error('[CallerID] localStorage yazma hatası:', e);
            }

            playNotificationSoundRepeat(3);
            touchGlobalInteraction();
            setPersistentPhoneNumber(incomingPhone);
            setIncomingCall({ phoneNumber: incomingPhone, timestamp, callId });

            const customEvent = new CustomEvent('callerIdIncoming', {
              detail: { phoneNumber: incomingPhone, timestamp, callId },
            });
            window.dispatchEvent(customEvent);
          } catch (e) {
            console.error('[CallerID] JSON parse hatası:', e);
          }
        };

        ws.onerror = (error) => {
          console.error('[CallerID] WebSocket error:', error);
          setIsCallerIDConnected(false);
        };

        ws.onclose = (event) => {
          setIsCallerIDConnected(false);

          if (isIntentionallyClosedRef.current) {
            console.log('[CallerID] WebSocket kapandı (intentional)');
            return;
          }

          console.log(`[CallerID] WebSocket kapandı (code=${event.code}, reason=${event.reason})`);
          scheduleReconnect('onclose');
        };
      } catch (e) {
        console.error('[CallerID] WebSocket create hatası:', e);
        setIsCallerIDConnected(false);
        scheduleReconnect('create_failed');
      }
    };

    console.log('[CallerID] WebSocket stabil bağlantı başlatılıyor...');
    connectWebSocket();

    // Cleanup
    return () => {
      console.log('[CallerID] Cleanup');
      isIntentionallyClosedRef.current = true;
      setIsCallerIDConnected(false);
      cleanupSocket();
    };
  }, [isAuthenticated]);

  // Polling fallback: WS kaçırırsa son çağrıları çekip tamamlar
  useEffect(() => {
    if (!isAuthenticated) return;

    let isCancelled = false;

    const poll = async () => {
      try {
        const params = new URLSearchParams();
        if (callerPollingAfter) params.set('after', callerPollingAfter);
        params.set('limit', '100');

        const url = `${API}/caller-id/recent?${params.toString()}`;
        const res = await axios.get(url);

        if (isCancelled) return;
        if (!res.data?.success) return;

        const calls = Array.isArray(res.data.calls) ? res.data.calls : [];
        if (calls.length === 0) return;

        // En güncel timestamp'i anchor yap
        const latest = calls[calls.length - 1];
        if (latest?.timestamp) {
          setCallerPollingAfter(latest.timestamp);
        }

        // Her çağrıyı sırayla işle (dedup ile)
        for (const c of calls) {
          const callId = c.call_id;
          const phone = c.phone_number;
          if (!callId || !phone) continue;

          if (processedCallIdsRef.current.has(callId)) continue;
          processedCallIdsRef.current.add(callId);

          const timestampMs = Date.now();

          try {
            const callData = { phoneNumber: phone, timestamp: timestampMs, callId, processed: false };
            localStorage.setItem(callId, JSON.stringify(callData));
            localStorage.setItem('latestCallId', callId);
          } catch (e) {
            // ignore
          }

          playNotificationSoundRepeat(3);
          touchGlobalInteraction();
          setPersistentPhoneNumber(phone);
          setIncomingCall({ phoneNumber: phone, timestamp: timestampMs, callId });

          const customEvent = new CustomEvent('callerIdIncoming', {
            detail: { phoneNumber: phone, timestamp: timestampMs, callId },
          });
          window.dispatchEvent(customEvent);
        }
      } catch (e) {
        // Sessiz hata - polling devam etsin
      }
    };

    const interval = setInterval(poll, 1000);
    poll();

    return () => {
      isCancelled = true;
      clearInterval(interval);
    };
  }, [isAuthenticated, callerPollingAfter]);

  // Bildirim sesi - tekrarlı çalma
  const playNotificationSoundRepeat = (times) => {
    let count = 0;
    const interval = setInterval(() => {
      if (count >= times) {
        clearInterval(interval);
        return;
      }
      
      // Basit beep sesi
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800; // Frekans
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
      
      count++;
    }, 600); // 600ms aralıklarla çal
  };

  // If checking auth, show loading
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, show login
  if (!isAuthenticated) {
    return (
      <LoginPage
        onLoginSuccess={() => {
          setIsAuthenticated(true);
        }}
      />
    );
  }

  return (
    <div className="App">
      {/* Global Telefon Çağrısı Bildirimi - Tüm sayfalarda gösterilir */}
      {incomingCall && (
        <div className="fixed top-4 right-4 z-[100] bg-green-500 text-white px-6 py-4 rounded-lg shadow-2xl animate-bounce">
          <div className="flex items-center space-x-3">
            <div className="text-2xl">📞</div>
            <div>
              <div className="font-bold text-lg">Gelen Arama!</div>
              <div className="text-sm">{incomingCall.phoneNumber}</div>
            </div>
            <button
              onClick={() => setIncomingCall(null)}
              className="ml-4 bg-white text-green-500 rounded-full w-6 h-6 flex items-center justify-center hover:bg-gray-100"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <BrowserRouter>
        <Layout onLogout={() => setIsAuthenticated(false)}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route 
              path="/telefon" 
              element={
                <PhonePage 
                  incomingCall={incomingCall}
                  isCallerIDConnected={isCallerIDConnected}
                  clearIncomingCall={() => {
                    touchGlobalInteraction();
                    setIncomingCall(null);
                  }}
                  persistentPhoneNumber={persistentPhoneNumber}
                  setPersistentPhoneNumber={(v) => {
                    touchGlobalInteraction();
                    setPersistentPhoneNumber(v);
                  }}
                />
              } 
            />
            <Route path="/siparis" element={<OrderPage />} />
            <Route path="/masalar" element={<TablesPage />} />
            <Route path="/musteri" element={<CustomerPage />} />
            <Route path="/raporlar" element={<ReportsPage />} />
            <Route path="/admin" element={<AdminPage />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </div>
  );
}

export default App;